/**
 * DashboardShell — the authenticated dashboard layout with sidebar.
 *
 * This wraps the existing dashboard views with proper auth-aware navigation.
 * It uses the AuthContext for user info, org switching, and logout.
 *
 * RC2 additions:
 * - Mobile responsive sidebar (hamburger + slide-in drawer + backdrop)
 * - Outside click dismissal for dropdowns
 * - Escape key handling for dropdowns
 * - Keyboard accessibility (focus trap, aria attributes)
 */

import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useClickOutside } from '../hooks/use-click-outside';
import { useModal } from './Modal';
import { useToast } from './Toast';

interface NavItem {
  readonly label: string;
  readonly value: string;
  readonly status: 'v1' | 'coming-soon';
  readonly note?: string;
}

export const NAV: readonly NavItem[] = [
  { label: 'Overview', value: 'overview', status: 'v1' },
  { label: 'Applications', value: 'applications', status: 'v1' },
  { label: 'Consent Log', value: 'consent-log', status: 'v1' },
  { label: 'Consent Notice Designer', value: 'notice-manager', status: 'v1' },
  { label: 'Compliance Certificate', value: 'compliance-certificate', status: 'v1' },
  { label: 'API Keys', value: 'api-keys', status: 'v1' },
  { label: 'Settings', value: 'settings', status: 'v1' },
  {
    label: 'Data Rights & Grievance',
    value: 'data-rights',
    status: 'coming-soon',
    note: 'Future Roadmap (Stage 2)',
  },
  {
    label: 'Breach Response',
    value: 'breach-response',
    status: 'coming-soon',
    note: 'Future Roadmap (Stage 2)',
  },
  {
    label: "Children's Consent",
    value: 'childrens-consent',
    status: 'coming-soon',
    note: 'Future Roadmap (Stage 2)',
  },
];

interface DashboardShellProps {
  currentView: string;
  onNavigate: (view: string) => void;
  activeAppId: string;
  children: React.ReactNode;
}

export function DashboardShell({
  currentView,
  onNavigate,
  activeAppId,
  children,
}: DashboardShellProps) {
  const { user, organisations, activeOrgId, setActiveOrgId, logout } = useAuth();
  const { addToast } = useToast();
  const { openModal } = useModal();
  const navigate = useNavigate();
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const orgDropdownRef = useRef<HTMLDivElement>(null);
  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const activeOrg = organisations.find((o) => o.id === activeOrgId);

  // Close dropdowns on outside click or Escape
  const closeOrgDropdown = useCallback(() => setOrgDropdownOpen(false), []);
  const closeProfileDropdown = useCallback(() => setProfileOpen(false), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  useClickOutside([orgDropdownRef], closeOrgDropdown, { escapeKey: true });
  useClickOutside([profileDropdownRef], closeProfileDropdown, { escapeKey: true });

  const handleNavigate = useCallback(
    (view: string) => {
      onNavigate(view);
      setSidebarOpen(false);
    },
    [onNavigate],
  );

  const handleLogout = () => {
    setProfileOpen(false);
    openModal({
      title: 'Log Out?',
      description: 'You will need to sign in again to access your dashboard.',
      confirmText: 'Log Out',
      cancelText: 'Cancel',
      variant: 'destructive',
      onConfirm: async () => {
        await logout();
        navigate('/login');
        addToast({ type: 'success', message: 'Logged out successfully' });
      },
    });
  };

  const sidebarNav = (
    <nav className="mt-6 flex flex-col gap-1" role="navigation" aria-label="Dashboard navigation">
      {NAV.map((item) => (
        <button
          type="button"
          key={item.label}
          disabled={item.status === 'coming-soon'}
          onClick={() => handleNavigate(item.value)}
          className={`flex items-center justify-between rounded-xl px-4 py-3 text-sm transition ${
            item.status === 'coming-soon'
              ? 'text-slate-600 cursor-not-allowed'
              : currentView === item.value
                ? 'bg-blue-600/10 text-blue-400 border border-blue-500/20'
                : 'text-slate-400 hover:bg-slate-900/50 hover:text-slate-100'
          }`}
          aria-current={currentView === item.value ? 'page' : undefined}
        >
          <span>{item.label}</span>
          {item.status === 'coming-soon' && (
            <span className="rounded bg-slate-950 border border-slate-800 px-1.5 py-0.5 text-[8px] uppercase tracking-wider text-slate-500 font-bold">
              Soon
            </span>
          )}
        </button>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-slate-950 text-slate-100 font-sans">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between border-b border-slate-900 bg-slate-950/95 backdrop-blur-sm px-4 py-3">
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="rounded-lg p-2 text-slate-400 hover:bg-slate-900 hover:text-slate-100 transition"
          aria-label="Open navigation menu"
          aria-expanded={sidebarOpen}
          aria-controls="sidebar-drawer"
        >
          {/* Hamburger icon */}
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
            />
          </svg>
        </button>
        <div className="flex items-center space-x-2">
          <div className="h-4 w-4 rounded bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]"></div>
          <h1 className="text-sm font-bold tracking-wider uppercase bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">
            DPDP Consent
          </h1>
        </div>
        <div className="w-10"></div>
      </header>

      {/* Mobile Sidebar Backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-950/60 backdrop-blur-sm lg:hidden"
          aria-hidden="true"
          onClick={closeSidebar}
        />
      )}

      {/* Desktop Sidebar */}
      <aside
        className="hidden lg:flex w-64 flex-shrink-0 border-r border-slate-900 bg-slate-900/30 p-6 flex-col justify-between"
        aria-label="Sidebar"
      >
        <div>
          <div className="flex items-center space-x-2">
            <div className="h-4 w-4 rounded bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]"></div>
            <h1 className="text-lg font-bold tracking-wider uppercase bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">
              DPDP Consent
            </h1>
          </div>
          <p className="mt-1 text-[10px] text-slate-500 tracking-widest uppercase">
            Compliance Engine
          </p>

          {/* Organisation Switcher */}
          {organisations.length > 0 && (
            <div className="mt-6">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-2">
                Organisation
              </p>
              <div className="relative" ref={orgDropdownRef}>
                <button
                  type="button"
                  onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
                  aria-haspopup="listbox"
                  aria-expanded={orgDropdownOpen}
                  aria-label={`Select organisation. Current: ${activeOrg?.name || 'Select org'}`}
                  className="w-full flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950 px-3 py-2.5 text-sm text-slate-200 hover:border-slate-700 transition"
                >
                  <span className="truncate">{activeOrg?.name || 'Select org'}</span>
                  <span className="text-slate-500 text-xs" aria-hidden="true">
                    ▼
                  </span>
                </button>
                {orgDropdownOpen && (
                  <div
                    className="absolute z-50 mt-1 w-full rounded-xl border border-slate-800 bg-slate-900 shadow-2xl overflow-hidden"
                    role="listbox"
                    aria-label="Organisation list"
                  >
                    {organisations.map((org) => (
                      <button
                        type="button"
                        key={org.id}
                        role="option"
                        aria-selected={org.id === activeOrgId}
                        onClick={() => {
                          setActiveOrgId(org.id);
                          setOrgDropdownOpen(false);
                          addToast({
                            type: 'success',
                            message: `Switched to ${org.name}`,
                          });
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-800 transition ${
                          org.id === activeOrgId ? 'text-blue-400 bg-blue-600/10' : 'text-slate-300'
                        }`}
                      >
                        {org.name}
                        <span className="block text-[10px] text-slate-500 capitalize">
                          {org.plan_tier} plan
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {sidebarNav}
        </div>

        {/* Footer: App ID + User profile */}
        <div className="border-t border-slate-900 pt-6 space-y-4">
          {activeAppId && (
            <div>
              <p className="text-xs text-slate-500 font-semibold">Active App ID:</p>
              <div className="mt-1 flex items-center gap-2 rounded-lg border border-slate-900 bg-slate-950 p-2">
                <p className="flex-1 truncate font-mono text-[10px] text-slate-400">
                  {activeAppId}
                </p>
              </div>
            </div>
          )}

          {/* User Profile */}
          {user && (
            <div className="relative" ref={profileDropdownRef}>
              <button
                type="button"
                onClick={() => setProfileOpen(!profileOpen)}
                aria-haspopup="true"
                aria-expanded={profileOpen}
                aria-label={`User menu for ${user.fullName || user.email}`}
                className="w-full flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-950 p-2.5 hover:border-slate-700 transition"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-xs font-bold text-white">
                  {(user.fullName || user.email)[0]?.toUpperCase()}
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="truncate text-xs font-semibold text-slate-200">
                    {user.fullName || 'User'}
                  </p>
                  <p className="truncate text-[10px] text-slate-500">{user.email}</p>
                </div>
                {!user.emailVerified && (
                  <span
                    className="text-[9px] text-yellow-400"
                    title="Email not verified"
                    aria-label="Email not verified"
                  >
                    ⚠
                  </span>
                )}
              </button>
              {profileOpen && (
                <div
                  className="absolute bottom-full mb-1 w-full rounded-xl border border-slate-800 bg-slate-900 shadow-2xl overflow-hidden"
                  role="menu"
                  aria-label="User actions"
                >
                  <div className="px-3 py-2 border-b border-slate-800">
                    <p className="text-xs font-semibold text-slate-200">
                      {user.fullName || 'User'}
                    </p>
                    <p className="text-[10px] text-slate-500">{user.email}</p>
                    {!user.emailVerified && (
                      <p className="mt-1 text-[10px] text-yellow-400">⚠ Email not verified</p>
                    )}
                  </div>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleLogout}
                    className="w-full px-3 py-2.5 text-left text-xs text-red-400 hover:bg-red-950/20 transition"
                  >
                    Log Out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>

      {/* Mobile Sidebar Drawer */}
      <aside
        id="sidebar-drawer"
        ref={sidebarRef}
        className={`lg:hidden fixed top-0 left-0 bottom-0 w-72 bg-slate-950 border-r border-slate-900 z-50 transform transition-transform duration-300 ease-in-out flex flex-col justify-between p-6 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label="Mobile navigation"
        aria-hidden={!sidebarOpen}
      >
        <div>
          {/* Close button */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <div className="h-4 w-4 rounded bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]"></div>
              <h1 className="text-sm font-bold tracking-wider uppercase bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">
                DPDP Consent
              </h1>
            </div>
            <button
              type="button"
              onClick={closeSidebar}
              className="rounded-lg p-2 text-slate-400 hover:bg-slate-900 hover:text-slate-100 transition"
              aria-label="Close navigation menu"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Organisation Switcher (Mobile) */}
          {organisations.length > 0 && (
            <div className="mb-6">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-2">
                Organisation
              </p>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
                  aria-haspopup="listbox"
                  aria-expanded={orgDropdownOpen}
                  className="w-full flex items-center justify-between rounded-xl border border-slate-800 bg-slate-900 px-3 py-2.5 text-sm text-slate-200 hover:border-slate-700 transition"
                >
                  <span className="truncate">{activeOrg?.name || 'Select org'}</span>
                  <span className="text-slate-500 text-xs" aria-hidden="true">
                    ▼
                  </span>
                </button>
                {orgDropdownOpen && (
                  <div className="absolute z-50 mt-1 w-full rounded-xl border border-slate-800 bg-slate-900 shadow-2xl overflow-hidden">
                    {organisations.map((org) => (
                      <button
                        type="button"
                        key={org.id}
                        onClick={() => {
                          setActiveOrgId(org.id);
                          setOrgDropdownOpen(false);
                          addToast({
                            type: 'success',
                            message: `Switched to ${org.name}`,
                          });
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-800 transition ${
                          org.id === activeOrgId ? 'text-blue-400 bg-blue-600/10' : 'text-slate-300'
                        }`}
                      >
                        {org.name}
                        <span className="block text-[10px] text-slate-500 capitalize">
                          {org.plan_tier} plan
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {sidebarNav}
        </div>

        {/* Mobile footer */}
        <div className="border-t border-slate-900 pt-6 space-y-4">
          {activeAppId && (
            <div>
              <p className="text-xs text-slate-500 font-semibold">Active App ID:</p>
              <div className="mt-1 flex items-center gap-2 rounded-lg border border-slate-900 bg-slate-900 p-2">
                <p className="flex-1 truncate font-mono text-[10px] text-slate-400">
                  {activeAppId}
                </p>
              </div>
            </div>
          )}

          {user && (
            <div>
              <div className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900 p-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-xs font-bold text-white">
                  {(user.fullName || user.email)[0]?.toUpperCase()}
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="truncate text-xs font-semibold text-slate-200">
                    {user.fullName || 'User'}
                  </p>
                  <p className="truncate text-[10px] text-slate-500">{user.email}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="mt-3 w-full px-3 py-2.5 text-left text-xs text-red-400 hover:bg-red-950/20 rounded-xl transition"
              >
                Log Out
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-4 sm:p-6 lg:p-10 overflow-y-auto lg:pt-10 pt-16">{children}</main>
    </div>
  );
}
